import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import Swal from 'sweetalert2';

// Initialize Worker once (pdfjs-dist 4.x ships .mjs worker files)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const extractTextFromPDF = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(' ');
      if (pageText.trim()) fullText += `--- Page ${i} ---\n${pageText}\n\n`;
    }
    return fullText.trim() || null;
  } catch (err) {
    console.error('PDF Parse Error:', err);
    return null;
  }
};

export const extractTextFromDocx = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return String(result?.value || '').trim() || null;
  } catch (err) {
    console.error('DOCX Parse Error:', err);
    return null;
  }
};

export const processUploadedFiles = async (files) => {
  const contents = [];
  let errorCount = 0;

  for (const file of files) {
    try {
      let text = '';
      const lowerName = String(file?.name || '').toLowerCase();
      const fileType = String(file?.type || '').toLowerCase();

      if (fileType.includes('pdf') || lowerName.endsWith('.pdf')) {
        text = await extractTextFromPDF(file);
        if (text === null) {
          errorCount++;
          continue;
        }
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        lowerName.endsWith('.docx')
      ) {
        text = await extractTextFromDocx(file);
        if (text === null) {
          errorCount++;
          continue;
        }
      } else {
        text = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsText(file);
        });
      }
      if (text && text.trim()) {
        const fileHeader = files.length > 1 ? `### Source Material: ${file.name}\n\n` : '';
        contents.push(`${fileHeader}${text.trim()}`);
      }
    } catch (e) {
      console.error(e);
      errorCount++;
    }
  }

  if (contents.length === 0 && errorCount > 0) {
    Swal.fire({ icon: 'warning', title: 'Read Error', text: 'Some files could not be read.' });
    return null;
  }
  return contents.join('\n\n---\n\n');
};

const countKeywordHits = (text, keywords) => {
  let hits = 0;
  for (const kw of keywords) {
    if (text.includes(kw)) hits += 1;
  }
  return hits;
};

export const validateAcademicStudyMaterial = (rawText) => {
  const original = String(rawText || "");
  const text = original.toLowerCase().replace(/\s+/g, " ").trim();
  const lines = original
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!text) {
    return { isValid: false, title: "Empty File", message: "No readable content was extracted from the uploaded file." };
  }

  const resumeSignals = [
    "curriculum vitae",
    "resume",
    "professional summary",
    "profile summary",
    "career objective",
    "objective",
    "work experience",
    "professional experience",
    "employment history",
    "job title",
    "technical skills",
    "hard skills",
    "soft skills",
    "core competencies",
    "certifications",
    "references",
    "linkedin",
    "portfolio",
    "languages",
    "education",
  ];

  const academicSignals = [
    "chapter",
    "module",
    "lesson",
    "introduction",
    "methodology",
    "literature review",
    "research",
    "hypothesis",
    "theorem",
    "definition",
    "equation",
    "example",
    "discussion",
    "conclusion",
    "study",
    "lecture",
    "exam",
    "quiz",
  ];

  const resumeHits = countKeywordHits(text, resumeSignals);
  const academicHits = countKeywordHits(text, academicSignals);
  const hasContactPattern =
    /(?:\+?\d[\d\s\-()]{7,}\d)/.test(text) ||
    /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/.test(text);
  const resumeSectionHeaderCount = lines.filter((line) =>
    /^(summary|professional summary|profile|objective|professional experience|work experience|employment history|education|skills|languages|certifications|references)\b[:\s-]*/i.test(
      line
    )
  ).length;
  const hasJobDatePattern =
    /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\.?\s*\d{2,4}\s*[–-]\s*(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|present|current)\.?\s*\d{0,4}\b/i.test(
      text
    ) ||
    /\b(20\d{2}|19\d{2})\s*[–-]\s*(20\d{2}|19\d{2}|present|current)\b/i.test(text);
  const hasResumeTemplateHint =
    text.includes("my-resume-templates.com") ||
    text.includes("name.surname@gmail.com") ||
    text.includes("curriculum vitae");

  const looksLikeResume =
    hasResumeTemplateHint ||
    (resumeSectionHeaderCount >= 3 && (hasJobDatePattern || hasContactPattern || resumeHits >= 3)) ||
    (resumeSectionHeaderCount >= 4) ||
    resumeHits >= 5 ||
    (resumeHits >= 3 && academicHits <= 1) ||
    (hasContactPattern && resumeHits >= 3 && academicHits <= 2);

  if (looksLikeResume) {
    return {
      isValid: false,
      title: "Non-Academic File Detected",
      message:
        "Uploaded file appears to be a resume/CV. Please upload academic material such as lecture notes, modules, chapters, or study guides.",
    };
  }

  return { isValid: true, title: "", message: "" };
};
